/**
 * Multi-Currency Display Component
 * Shows amounts in multiple currencies with conversion
 */

import React from 'react';
import { useCurrency, Currency } from '../hooks/useCurrency';

interface MultiCurrencyDisplayProps {
  amount: number;
  baseCurrency: Currency;
  showAllCurrencies?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const MultiCurrencyDisplay: React.FC<MultiCurrencyDisplayProps> = ({
  amount,
  baseCurrency,
  showAllCurrencies = false,
  className = '',
  size = 'md'
}) => {
  const { selectedCurrency, formatAmount, convertAmount, exchangeRates } = useCurrency();

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  const primaryAmount = convertAmount(amount, baseCurrency, selectedCurrency);
  const formattedPrimary = formatAmount(primaryAmount, selectedCurrency);

  if (!showAllCurrencies) {
    return (
      <span className={`font-medium ${sizeClasses[size]} ${className}`}>
        {formattedPrimary}
      </span>
    );
  }

  // Show all currencies
  const currencies: Currency[] = ['HBAR', 'USD', 'INR'];
  const conversions = currencies.map(currency => ({
    currency,
    amount: convertAmount(amount, baseCurrency, currency),
    formatted: formatAmount(convertAmount(amount, baseCurrency, currency), currency)
  }));

  return (
    <div className={`${className}`}>
      <div className={`font-medium ${sizeClasses[size]} text-gray-900`}>
        {formattedPrimary}
      </div>
      {exchangeRates && (
        <div className="mt-1 space-y-1">
          {conversions
            .filter(conv => conv.currency !== selectedCurrency)
            .map(conv => (
              <div key={conv.currency} className="text-xs text-gray-500">
                {conv.formatted}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

interface CurrencyComparisonProps {
  amounts: Array<{
    label: string;
    amount: number;
    baseCurrency: Currency;
    color?: string;
  }>;
  className?: string;
}

export const CurrencyComparison: React.FC<CurrencyComparisonProps> = ({
  amounts,
  className = ''
}) => {
  const { selectedCurrency, formatAmount, convertAmount } = useCurrency();

  return (
    <div className={`space-y-2 ${className}`}>
      {amounts.map((item, index) => {
        const convertedAmount = convertAmount(item.amount, item.baseCurrency, selectedCurrency);
        const formatted = formatAmount(convertedAmount, selectedCurrency);
        
        return (
          <div key={index} className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{item.label}</span>
            <span 
              className={`font-medium ${item.color || 'text-gray-900'}`}
            >
              {formatted}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface CurrencyTrendProps {
  data: Array<{
    date: string;
    hbar?: number;
    usd?: number;
    inr?: number;
  }>;
  className?: string;
}

export const CurrencyTrend: React.FC<CurrencyTrendProps> = ({
  data,
  className = ''
}) => {
  const { selectedCurrency, formatAmount } = useCurrency();

  const getAmountForCurrency = (item: any, currency: Currency): number => {
    switch (currency) {
      case 'HBAR':
        return item.hbar || item.totalCostHBAR || 0;
      case 'USD':
        return item.usd || item.totalCostUSD || 0;
      case 'INR':
        return item.inr || item.totalCostINR || 0;
      default:
        return 0;
    }
  };

  return (
    <div className={`${className}`}>
      {data.map((item, index) => {
        const amount = getAmountForCurrency(item, selectedCurrency);
        const formatted = formatAmount(amount, selectedCurrency);
        
        return (
          <div key={index} className="flex justify-between items-center py-1">
            <span className="text-sm text-gray-600">{item.date}</span>
            <span className="font-medium text-gray-900">{formatted}</span>
          </div>
        );
      })}
    </div>
  );
};

export default MultiCurrencyDisplay;